import type { FormInstance, FormProps } from "antd";
import type { KeyboardEventHandler } from "react";

import { Form } from "antd";

import { useAppNotification } from "@/modules/app-notification";

const { useForm, useWatch } = Form;

/**
 * This hook is intended for use outside of <CommentForm>:
 *
 * const { form, onSuccess, onError } = useCommentForm();
 * const { mutate, isPending } = useMutation();
 * const onFormSubmit = ({ comment }) => mutate({ comment }, { onSuccess, onError });
 * return (<CommentForm form={form} isLoading={isLoading} onFinish={onFormSubmit} />);
 */
export const useCommentForm = () => {
  const [form] = useForm();
  const { notify } = useAppNotification();

  const onFormSuccess = () => {
    form.resetFields();
  };

  const onFormError = () => {
    notify.error();
  };

  return {
    form,
    onFormSuccess,
    onFormError,
  };
};

/**
 * This hook is intended to be used directly in <CommentForm>
 */
export const useCommentFormLayout = (form: FormInstance) => {
  const isFocus = useWatch("focus", form);
  const formLayout: FormProps["layout"] = isFocus ? "vertical" : "inline";
  const fieldRows = isFocus ? 3 : 1;

  const setFocus = (value: boolean) => {
    form.setFieldValue("focus", value);
  };

  const reset = () => {
    setFocus(false);
    form.resetFields();
  };

  const onFieldKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === "Escape") reset();
  };

  const onFieldFocus = () => {
    setFocus(true);
  };

  const onCancelClick = () => {
    reset();
  };

  return {
    formLayout,
    fieldRows,
    onFieldFocus,
    onFieldKeyDown,
    onCancelClick,
  };
};
